import React, { useState, useEffect } from 'react';

/**
 * Audit log table component with sortable columns and pagination
 */
const AuditLogTable = ({
  logs,
  loading,
  pagination,
  onPageChange,
  onSort,
  theme = 'dark'
}) => {
  const [expandedLogs, setExpandedLogs] = useState(new Set());

  // Reset expanded logs when logs change (e.g., pagination, filtering)
  useEffect(() => {
    setExpandedLogs(new Set());
  }, [logs]);

  /**
   * Handle sort column click
   */
  const handleSort = (column) => {
    const newOrder = pagination.sortBy === column && pagination.sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(column, newOrder);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  /**
   * Get status badge styling
   */
  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium border';
    
    switch (status?.toLowerCase()) {
      case 'success':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-green-900/30 text-green-400 border-green-500/30'
            : 'bg-green-100 text-green-800 border-green-200'
        }`;
      case 'failure':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-red-900/30 text-red-400 border-red-500/30'
            : 'bg-red-100 text-red-800 border-red-200'
        }`;
      case 'partial':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30'
            : 'bg-yellow-100 text-yellow-800 border-yellow-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-gray-900/30 text-gray-400 border-gray-500/30'
            : 'bg-gray-100 text-gray-600 border-gray-200'
        }`;
    }
  };

  /**
   * Get operation type badge styling
   */
  const getOperationBadge = (operationType) => {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium border';
    
    switch (operationType?.toLowerCase()) {
      case 'create':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-blue-900/30 text-blue-400 border-blue-500/30'
            : 'bg-blue-100 text-blue-800 border-blue-200'
        }`;
      case 'update':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-purple-900/30 text-purple-400 border-purple-500/30'
            : 'bg-purple-100 text-purple-800 border-purple-200'
        }`;
      case 'delete':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-red-900/30 text-red-400 border-red-500/30'
            : 'bg-red-100 text-red-800 border-red-200'
        }`;
      case 'read':
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-teal-900/30 text-teal-400 border-teal-500/30'
            : 'bg-teal-100 text-teal-800 border-teal-200'
        }`;
      default:
        return `${baseClasses} ${
          theme === 'dark'
            ? 'bg-gray-900/30 text-gray-400 border-gray-500/30'
            : 'bg-gray-100 text-gray-600 border-gray-200'
        }`;
    }
  };

  /**
   * Render sort icon
   */
  const SortIcon = ({ column, currentSort, sortOrder }) => {
    if (currentSort !== column) {
      return <span className={`ml-1 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>↕️</span>;
    }
    return (
      <span className="text-teal-400 ml-1">
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  /**
   * Toggle expanded log details
   */
  const toggleExpanded = (logId) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  /**
   * Render expanded log details
   */
  const renderExpandedDetails = (log) => {
    return (
      <tr className={theme === 'dark' ? 'bg-gray-900/50' : 'bg-gray-50'}>
        <td colSpan="7" className="px-4 py-4">
          <div className="space-y-4">
            {/* Old and New Values */}
            {(log.old_values || log.new_values) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {log.old_values && (
                  <div>
                    <h4 className={`text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>Previous Values</h4>
                    <pre className={`p-3 rounded text-xs overflow-x-auto ${
                      theme === 'dark'
                        ? 'bg-gray-800 text-gray-300'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {JSON.stringify(typeof log.old_values === 'string' ? JSON.parse(log.old_values) : log.old_values, null, 2)}
                    </pre>
                  </div>
                )}
                {log.new_values && (
                  <div>
                    <h4 className={`text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>New Values</h4>
                    <pre className={`p-3 rounded text-xs overflow-x-auto ${
                      theme === 'dark'
                        ? 'bg-gray-800 text-gray-300'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {JSON.stringify(typeof log.new_values === 'string' ? JSON.parse(log.new_values) : log.new_values, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Metadata */}
            {log.metadata && (
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>Metadata</h4>
                <pre className={`p-3 rounded text-xs overflow-x-auto ${
                  theme === 'dark'
                    ? 'bg-gray-800 text-gray-300'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {JSON.stringify(typeof log.metadata === 'string' ? JSON.parse(log.metadata) : log.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* Error Message */}
            {log.error_message && (
              <div>
                <h4 className={`text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-red-400' : 'text-red-600'
                }`}>Error Details</h4>
                <div className={`p-3 rounded text-sm border ${
                  theme === 'dark'
                    ? 'bg-red-900/20 border-red-500/30 text-red-300'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {log.error_message}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  /**
   * Render pagination controls
   */
  const PaginationControls = () => {
    const { page, totalPages, total, limit } = pagination;
    // Fallback calculation if totalPages is not provided
    const calculatedTotalPages = totalPages || Math.ceil(total / limit) || 1;
    const pages = [];
    
    const startPage = Math.max(1, page - 2);
    const endPage = Math.min(calculatedTotalPages, page + 2);
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className={`flex items-center justify-between px-4 py-3 border-t ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Previous
          </button>
          
          {startPage > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                1
              </button>
              {startPage > 2 && <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>...</span>}
            </>
          )}
          
          {pages.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                pageNum === page
                  ? 'bg-teal-600 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
          
          {endPage < calculatedTotalPages && (
            <>
              {endPage < calculatedTotalPages - 1 && <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}>...</span>}
              <button
                onClick={() => onPageChange(calculatedTotalPages)}
                className={`px-3 py-1 text-sm rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {calculatedTotalPages}
              </button>
            </>
          )}
          
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= calculatedTotalPages}
            className={`px-3 py-1 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Next
          </button>
        </div>
        
        <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          Page {page} of {calculatedTotalPages} ({pagination.total} total entries)
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`rounded-lg border ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-400 mx-auto"></div>
          <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Loading audit logs...</p>
        </div>
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className={`rounded-lg border ${
        theme === 'dark'
          ? 'bg-gray-800 border-gray-700'
          : 'bg-white border-gray-200'
      }`}>
        <div className="p-8 text-center">
          <div className="text-4xl mb-4">📋</div>
          <h3 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>No audit logs found</h3>
          <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
            No audit logs match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${
      theme === 'dark'
        ? 'bg-gray-800 border-gray-700'
        : 'bg-white border-gray-200'
    }`}>
      {/* Table header */}
      <div className={`px-4 py-3 border-b ${
        theme === 'dark'
          ? 'bg-gray-900 border-gray-700'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Audit Logs ({pagination.total})
          </h3>
          
          {logs.length > 0 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setExpandedLogs(new Set(logs.map((log, index) => 
                  log.id || `fallback-${index}-${log.operation_type}-${log.created_at}`
                )))}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Expand All
              </button>
              <button
                onClick={() => setExpandedLogs(new Set())}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Collapse All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={`border-b ${
            theme === 'dark'
              ? 'bg-gray-900 border-gray-700'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <tr>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Details
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => handleSort('created_at')}
              >
                Timestamp
                <SortIcon column="created_at" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => handleSort('operation_type')}
              >
                Operation
                <SortIcon column="operation_type" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => handleSort('resource_type')}
              >
                Resource
                <SortIcon column="resource_type" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Admin User
              </th>
              <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                Target User
              </th>
              <th 
                className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                onClick={() => handleSort('status')}
              >
                Status
                <SortIcon column="status" currentSort={pagination.sortBy} sortOrder={pagination.sortOrder} />
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
            {logs.map((log, index) => {
              // Generate a fallback ID for logs without proper IDs
              const logId = log.id || `fallback-${index}-${log.operation_type}-${log.created_at}`;
              
              return (
              <React.Fragment key={logId}>
                <tr
                  className={`cursor-pointer transition-colors ${
                    expandedLogs.has(logId)
                      ? theme === 'dark'
                        ? 'bg-teal-900/20 border-l-4 border-l-teal-400'
                        : 'bg-teal-50 border-l-4 border-l-teal-400'
                      : theme === 'dark'
                        ? 'hover:bg-gray-700/50'
                        : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleExpanded(logId)}
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button className="text-teal-400 hover:text-teal-300">
                      {expandedLogs.has(logId) ? '▼' : '▶'}
                    </button>
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-sm ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {formatDate(log.created_at)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={getOperationBadge(log.operation_type)}>
                      {log.operation_type}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{log.resource_type}</div>
                    {log.resource_id && (
                      <div className={`text-xs truncate max-w-32 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        ID: {log.resource_id}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {log.user?.email || log.admin_email || 'System'}
                    </div>
                    {log.admin_id && (
                      <div className={`text-xs truncate max-w-32 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {log.admin_id}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {log.user?.email || log.user_email || 'N/A'}
                    </div>
                    {log.user_id && (
                      <div className={`text-xs truncate max-w-32 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {log.user_id}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(log.status)}>
                      {log.status}
                    </span>
                  </td>
                </tr>
                {expandedLogs.has(logId) && renderExpandedDetails(log)}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>



      {/* Pagination */}
      {(pagination.total > pagination.limit || pagination.totalPages > 1) && <PaginationControls />}
    </div>
  );
};

export default AuditLogTable;
